import React from 'react';
import { ColumnActionsMode, IColumn } from '@fluentui/react';
import { Button, Tooltip } from '@fluentui/react-components';
import { CheckmarkCircleColor } from '@fluentui/react-icons';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';
import { IEntityTerminationTask } from '../types';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';

const truncatedCellStyle: React.CSSProperties = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const onRenderTaskDescription = (
  item: IEntityTerminationTask,
  toggleEditTaskPanel: (item: IEntityTerminationTask) => void,
): JSX.Element => {
  return (
    <Tooltip withArrow content={item?.description} relationship="label">
      <Button
        appearance="transparent"
        style={{ padding: 0, justifyContent: 'start' }}
        onClick={() => toggleEditTaskPanel(item)}
      >
        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{item?.description}</span>
      </Button>
    </Tooltip>
  );
};

const onRenderCompletedStatus = (item: IEntityTerminationTask): JSX.Element => {
  return <>{item.completedDate && <CheckmarkCircleColor fontSize={20} />}</>;
};

const getColumns = (
  toggleEditTaskPanel: (item: IEntityTerminationTask) => void,
): Array<IColumn> => [
  {
    key: 'completed',
    name: 'Completion Status',
    fieldName: 'completed',
    minWidth: 30,
    maxWidth: 30,
    isResizable: false,
    onRender: (item) => onRenderCompletedStatus(item),
  },
  {
    key: 'taskDescription',
    name: 'Task Description',
    fieldName: 'description',
    minWidth: 300,
    maxWidth: 300,
    isResizable: !toggleEditTaskPanel,
    columnActionsMode: toggleEditTaskPanel ? undefined : ColumnActionsMode.disabled,
    onRender: (item) => onRenderTaskDescription(item, toggleEditTaskPanel),
  },
  {
    key: 'owner',
    name: 'Owner',
    fieldName: 'owner',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const owner = item.owner as IServiceContact;
      const display = Array.isArray(owner) ? owner.map((o) => o.name).join(', ') : owner?.text;
      return <span>{display}</span>;
    },
  },
  {
    key: 'additionalTask',
    name: 'Additional Task',
    fieldName: 'comment',
    minWidth: 300,
    maxWidth: 300,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const { comment: additionalTask } = item;
      if (!additionalTask) {
        return <span style={truncatedCellStyle} />;
      }
      return (
        <Tooltip withArrow content={additionalTask} relationship="label">
          <span style={truncatedCellStyle}>{additionalTask}</span>
        </Tooltip>
      );
    },
  },
  {
    key: 'assignee',
    name: 'Assignee(s)',
    fieldName: 'assignees',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const field = item.assignees ?? item.assignee;
      const display = Array.isArray(field) ? field.map((o) => o.name).join(', ') : field?.text;

      return <span>{display}</span>;
    },
  },
  {
    key: 'followUpFlagDate',
    name: 'Follow Up Flag Date',
    fieldName: 'followUpFlagDate',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const { followUpFlagDate } = item;
      const formattedDate = followUpFlagDate
        ? momentUtc(followUpFlagDate, StatDateFormats.TwoDigitDate)
        : '';
      return <span>{formattedDate}</span>;
    },
  },
  {
    key: 'completedDate',
    name: 'Completed Date',
    fieldName: 'completedDate',
    minWidth: 120,
    maxWidth: 120,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled,
    onRender: (item) => {
      const { completedDate } = item;
      const formattedDate = completedDate
        ? momentUtc(completedDate, StatDateFormats.TwoDigitDate)
        : '';
      return <span>{formattedDate}</span>;
    },
  },
];

export default getColumns;
